/**
 * Kickard PHASE 3.9A — Session tracking.
 *
 * Owns the per-tab session lifecycle:
 *   • sessionStart() — POSTs /api/sessions/start on app boot.
 *   • Heartbeat every 60s while the tab is visible.
 *   • Pause heartbeat when the tab is backgrounded, resume on visibility.
 *   • sessionEnd() via sendBeacon on 'pagehide'/'beforeunload'.
 *
 * Failures are silently swallowed — gameplay must never depend on this.
 */
import { getSessionId, track } from './telemetry';
import { loadGuest } from './guestIdentity';

declare const process: { env: { [key: string]: string | undefined } };
const BACKEND = process.env.REACT_APP_BACKEND_URL;
const HEARTBEAT_MS = 60_000;

let _heartbeatTimer: ReturnType<typeof setInterval> | null = null;
let _lastScreen: string | null = null;
let _started = false;
let _ended = false;

function _post(path: string, payload: any, useBeacon = false): void {
  const url = `${BACKEND}${path}`;
  const body = JSON.stringify(payload);
  try {
    if (useBeacon && typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
      const blob = new Blob([body], { type: 'text/plain' });
      navigator.sendBeacon(url, blob);
      return;
    }
    fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body,
      keepalive: true,
    }).catch(() => { /* swallow */ });
  } catch {
    /* swallow */
  }
}

export function setScreen(name: string): void {
  _lastScreen = name;
}

export function sessionStart(sourceScreen?: string): void {
  if (_started) return;
  _started = true;
  _ended = false;
  const guest = loadGuest();
  if (!guest?.guestId) return; // guest identity not ready yet — caller will retry
  const sid = getSessionId();
  if (sourceScreen) _lastScreen = sourceScreen;
  _post('/api/sessions/start', {
    sessionId: sid,
    guestId: guest.guestId,
    displayName: guest.displayName,
    sourceScreen: _lastScreen || sourceScreen || 'home',
    clientTime: new Date().toISOString(),
  });
  _scheduleHeartbeat();
}

export function sessionHeartbeat(): void {
  if (!_started || _ended) return;
  _post('/api/sessions/heartbeat', {
    sessionId: getSessionId(),
    sourceScreen: _lastScreen || undefined,
  });
}

export function sessionEnd(reason: string = 'explicit'): void {
  if (!_started || _ended) return;
  _ended = true;
  _stopHeartbeat();
  const sid = getSessionId();
  _post('/api/sessions/end', { sessionId: sid, reason }, /* useBeacon */ true);
}

function _scheduleHeartbeat(): void {
  _stopHeartbeat();
  _heartbeatTimer = setInterval(() => {
    if (typeof document !== 'undefined' && document.visibilityState === 'hidden') {
      return; // avoid spam while tab is backgrounded
    }
    sessionHeartbeat();
  }, HEARTBEAT_MS);
}

function _stopHeartbeat(): void {
  if (_heartbeatTimer) {
    clearInterval(_heartbeatTimer);
    _heartbeatTimer = null;
  }
}

/**
 * One-shot install: attaches visibility + unload handlers.
 * Safe to call multiple times (idempotent).
 */
let _installed = false;
export function installSessionLifecycle(sourceScreen?: string): void {
  if (typeof window === 'undefined' || _installed) return;
  _installed = true;

  window.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') {
      track('app_backgrounded');
    } else {
      track('app_resumed');
      // Immediate heartbeat on resume so backend knows we're alive.
      sessionHeartbeat();
    }
  });
  // pagehide fires before unload on mobile Safari & standard browsers.
  window.addEventListener('pagehide', () => sessionEnd('pagehide'));
  window.addEventListener('beforeunload', () => sessionEnd('beforeunload'));

  sessionStart(sourceScreen);
}
